<script setup lang="ts">
import { useBetslip } from "~/store/betslip";
import { useSettings } from "~/store/settings";
import { calculateTotalOdds } from "~/utils/betslip/odds";
import t from "~/utils/translate";
import betslipCalculator from "~/utils/betslip/betslipCalculations";
import formatNumber from "~/utils/formatNumber";
import { IBetslipWinboost } from "~/types/betslip/betslip-winboost";
import { calculateActualWager } from "~/utils/betslip/wager";
import { calculatePreBoostedPrice } from "~/utils/betslip/prices";
const betslip = useBetslip();
const route = useRoute();
const settings = useSettings();

interface ISelection {
	outcomeId: string;
	eventId: number;
	marketId: string;
	value: number;
	selected: boolean;
	payment: 1 | 2 | 4;
	canPlaceSportsBonusBet: boolean;
	isSportBonusAllowed: boolean;
	meetsMinSportsBonusOdds: boolean;
}

const nextPercentage = computed(() => {
	const config = settings.config?.winBoostConfig;
	const index = config?.WinBoostOffers.findIndex((x) => x.WinBoostValue === betslip.winBoostConfig?.WinBoostValue);
	if (betslip.selection.length <= 0) return config?.WinBoostOffers[0];
	if (index || index === 0 || index === -1) return config?.WinBoostOffers[index + 1];
});

const winBoostProgressText = computed(() => {
	if (nextPercentage.value) {
		return t("winboost-progress", {
			variables: [
				{ key: "currentPercentage", value: betslip.winBoostConfig?.WinBoostValue },
				{
					key: "additionalSelections",
					value:
						nextPercentage.value.Events -
						(betslip.winBoostConfig?.Events ||
							betslip.selection.filter(
								(x) =>
									x.selected &&
									(betslip.prices.get(x.outcomeId)?.priceDecimal || 0) >= (settings.config?.winBoostConfig.MinOddsPerEvent || 0) &&
									settings.config?.winBoostConfig.ExcludedMarkets &&
									!settings.config?.winBoostConfig.ExcludedMarkets.includes(betslip.markets.get(x.marketId)?.marketTypeCName || "")
							).length),
				},
				{ key: "nextPercentage", value: nextPercentage.value?.WinBoostValue },
				{ key: "odds", value: settings.config?.winBoostConfig.MinOddsPerEvent },
			],
		});
	}
});

const totalOdds = computed(() => {
	return calculateTotalOdds(
		betslip.activeSelections.map((x) => ({
			priceNumerator: betslip.prices.get(x.outcomeId)?.numerator || 0,
			priceDenominator: betslip.prices.get(x.outcomeId)?.denominator || 0,
		}))
	);
});

const actualWagerAmount = computed(() => {
	if (betslip.activeTab === "multi" && betslip.multiWagerAmount > 0) {
		return calculateActualWager(betslip.multiWagerAmount, betslip.multiBetPayment);
	} else {
		return calculateActualWager((betslip.selection[0] && betslip.selection[0].value) || 0, betslip.multiBetPayment);
	}
});

const winboostConfig: ComputedRef<IBetslipWinboost | undefined> = computed(() => {
	const winboostSettings = settings.config?.winBoostConfig;
	if (betslip.activeTab === "single") {
		return undefined;
	} else if (winboostSettings && winboostSettings?.ExcludedSports && winboostSettings?.ExcludedSports.includes(route.params.sportId as string))
		return undefined;

	if (winboostSettings) {
		const bets: {
			eventId: number;
			priceNumerator: number;
			priceDenominator: number;
		}[] = [];
		let eligibleBets = 0;
		betslip.activeSelections.forEach((bet) => {
			const price = betslip.prices.get(bet.outcomeId);
			const market = betslip.markets.get(bet.marketId);
			if (
				price &&
				winboostSettings &&
				price?.priceDecimal >= winboostSettings?.MinOddsPerEvent &&
				bet.selected &&
				!winboostSettings?.ExcludedMarkets?.includes(market?.marketTypeCName || "")
			) {
				bets.push({
					eventId: bet.eventId,
					priceNumerator: price?.numerator || 0,
					priceDenominator: price?.denominator || 0,
				});
				eligibleBets++;
			}
		});

		if (eligibleBets > winboostSettings.WinBoostOffers.length) {
			return { ...winboostSettings.WinBoostOffers[winboostSettings.WinBoostOffers.length - 1], bets };
		}

		return { ...winboostSettings.WinBoostOffers.filter((x) => x.Events === eligibleBets)[0], bets } || { WinBoostValue: 0, bets: [] };
	} else return undefined;
});

const betslipCalculations = computed(() => {
	const preBoostedBets = betslip.activeSelections.map((x) => {
		const outcome = betslip.outcomes.get(x.outcomeId);
		const price = betslip.prices.get(x.outcomeId);
		if (outcome?.isBoosted) {
			const preboostOdd = calculatePreBoostedPrice({
				priceNumerator: price?.numerator || 0,
				priceDenominator: price?.denominator || 0,
			});
			return { priceNumerator: preboostOdd.preBoostedPriceNumerator, priceDenominator: preboostOdd.preBoostedPriceDenominator };
		}
		return { priceNumerator: price?.numerator || 0, priceDenominator: price?.denominator || 0 };
	});
	const bets = betslip.activeSelections.map((x) => ({
		priceNumerator: betslip.prices.get(x.outcomeId)?.numerator || 0,
		priceDenominator: betslip.prices.get(x.outcomeId)?.denominator || 0,
	}));
	return betslipCalculator(
		preBoostedBets,
		bets,
		actualWagerAmount.value,
		betslip.multiBetPayment,
		winboostConfig.value || { WinBoostValue: 0, bets: [] }
	);
});

const hasMultiBetConflict = computed(() => betslip.activeSelections.some((betSelection, index, bets) => hasDuplicate(betSelection, index, bets)));
const hasDuplicate = (
	betSelection: { eventId: number; outcomeId: string; marketId: string },
	index: number,
	bets: { eventId: number; outcomeId: string; marketId: string }[]
) => {
	const conflicts: {
		eventId: number;
		outcomeId: string;
		marketId: string;
	}[] = [];
	bets.forEach((bet) => {
		if (!conflicts.includes(bet)) {
			const storeEvent = betslip.events.get(betSelection.eventId);
			if (storeEvent && ["golf", "formula-1"].includes(storeEvent?.sportId) && settings.isRadarEnabled) {
				const otherBets = bets.filter(
					(x) => x.eventId === betSelection.eventId && x.marketId === betSelection.marketId && x.outcomeId !== betSelection.outcomeId
				);
				if (otherBets.length > 0) {
					conflicts.push(betSelection);
				}
			} else {
				const otherBets = bets.filter((x) => x.eventId === betSelection.eventId && x.outcomeId !== betSelection.outcomeId);
				if (otherBets.length > 0) {
					conflicts.push(betSelection);
				}
			}
		}
	});

	return !!conflicts.length;
};
const isEditingBet = computed(() => {
	return !!route.query.isEditingBet;
});

const progressPercent = computed(() => {
	if (settings.config?.winBoostConfig.WinBoostOffers && betslip.winBoostConfig?.Events) {
		const maxOffer = settings.config?.winBoostConfig.WinBoostOffers[settings.config?.winBoostConfig.WinBoostOffers.length - 1];
		const maxBets = maxOffer?.Events;
		return Math.round((betslip.winBoostConfig?.Events / (maxBets || 0)) * 100);
	}
});

const fireEvent = () => {
	const nuxtApp = useNuxtApp();

	if (hasMultiBetConflict.value) {
		return nuxtApp.$messageHandler({
			message: "BetslipCount",
			data: {
				left: "",
				center: t("show betslip"),
				right: "",
				Count: betslip.activeSelections.length,
				display: betslipCalculations.value.totalPotentialReturn <= 0 || betslip.open,
				insuranceEnabled: settings.hasFeature("IsBetwayInsuranceEnabled"),
				insuranceActive: betslip.multiBetPayment === 1 && !!betslip.insurancePayouts.length && betslip.activeTab === "multi",
			},
		});
	}

	nuxtApp.$messageHandler({
		message: "BetslipCount",
		data: {
			left: `${formatNumber(actualWagerAmount.value, "currency")} x ${t("odds")} ${totalOdds.value.toFixed(2)}`,
			leftTop: `${formatNumber(actualWagerAmount.value, "currency")} x ${t("odds")} ${totalOdds.value.toFixed(2)}`,
			leftBottom: t("total-betway-return"),
			center: "",
			right: `${t("total-potential-return")} ${formatNumber(betslipCalculations.value.payoutAmount, "currency")}`,
			rightTop:
				betslipCalculations.value.totalPotentialReturnPreBoost !== betslipCalculations.value.payoutAmount
					? `${formatNumber(betslipCalculations.value.totalPotentialReturnPreBoost, "currency")}`
					: null,
			rightBottom: `${formatNumber(betslipCalculations.value.payoutAmount, "currency")}`,
			Count: betslip.activeSelections.length,
			display: betslipCalculations.value.totalPotentialReturn <= 0 || betslip.open,
			isEditingBet: isEditingBet.value,
			winBoostDescription: winBoostProgressText.value,
			winBoostPercentage: progressPercent.value || 0,
			insuranceEnabled: settings.hasFeature("IsBetwayInsuranceEnabled"),
			insuranceActive: betslip.multiBetPayment === 1 && !!betslip.insurancePayouts.length && betslip.activeTab === "multi",
		},
	});
};

const groupedWatchProperty = computed(() => {
	return `${totalOdds.value}|${actualWagerAmount.value}|${betslip.open}|${isEditingBet.value}`;
});

watch(
	groupedWatchProperty,
	() => {
		fireEvent();
	},
	{ immediate: true }
);
</script>

<template>
	<div />
</template>
